// CreateAlertDialog replaces upstream's three-step "Create New Alert" wizard:
// one dialog that picks the service (showing who it notifies first), then the
// summary and details, then opens the new alert.
import React, { useMemo, useState } from 'react'
import { gql, useMutation, useQuery } from 'urql'
import { useLocation } from 'wouter'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material'
import { Check } from '@mui/icons-material'
import { SeverityChip } from './AlertsPage'
import { SearchBox, useBrand } from './ui'

// GoAlert's urql client suspends by default; this dialog renders its own states.
const noSuspense = { suspense: false }

const servicesQuery = gql`
  query ErecordsCreateAlertServices {
    services(input: { first: 100 }) {
      nodes {
        id
        name
        escalationPolicy {
          id
          name
        }
        onCallUsers {
          userID
          userName
          stepNumber
        }
      }
    }
  }
`
const createMutation = gql`
  mutation ErecordsCreateAlert($input: CreateAlertInput!) {
    createAlert(input: $input) {
      id
      alertID
    }
  }
`

type Service = {
  id: string
  name: string
  escalationPolicy: { id: string; name: string } | null
  onCallUsers: { userID: string; userName: string; stepNumber: number }[]
}

const firstNotified = (s?: Service): string[] =>
  Array.from(
    new Set(
      (s?.onCallUsers ?? [])
        .filter((u) => u.stepNumber === 0)
        .map((u) => u.userName),
    ),
  )

export default function CreateAlertDialog(props: {
  serviceID?: string // fixed service (service page)
  onlyIDs?: string[] // limit the choice (policy page)
  onClose: () => void
}): React.JSX.Element {
  const c = useBrand()
  const [, navigate] = useLocation()
  const [q] = useQuery({ query: servicesQuery, context: noSuspense })
  const [m, create] = useMutation(createMutation)
  const [serviceID, setServiceID] = useState(props.serviceID ?? '')
  const [search, setSearch] = useState('')
  const [summary, setSummary] = useState('')
  const [details, setDetails] = useState('')

  const all: Service[] = useMemo(
    () =>
      ((q.data?.services?.nodes ?? []) as Service[])
        .filter((s) => !props.onlyIDs || props.onlyIDs.includes(s.id))
        .sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
        ),
    [q.data, props.onlyIDs],
  )
  const shown = all.filter((s) =>
    s.name.toLowerCase().includes(search.toLowerCase()),
  )
  const picked = all.find((s) => s.id === serviceID)
  const who = firstNotified(picked)
  const valid = Boolean(serviceID) && summary.trim() !== ''

  const submit = (): void => {
    create(
      {
        input: {
          serviceID,
          summary: summary.trim(),
          details: details.trim(),
        },
      },
      { additionalTypenames: ['Alert'] },
    ).then((r) => {
      const id = r.data?.createAlert?.alertID
      if (r.error) return
      props.onClose()
      if (id) navigate(`/alerts/${id}`)
    })
  }

  return (
    <Dialog open onClose={props.onClose} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>Raise an alert</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 14, color: c.muted, mb: 2 }}>
          It goes through the service&apos;s escalation policy like any other
          alert: people are notified until someone acknowledges it.
        </Typography>

        <Typography sx={{ fontWeight: 600, mb: 1 }}>Service</Typography>
        {props.serviceID ? (
          <Box
            sx={{
              p: 1.5,
              borderRadius: '12px',
              border: `1px solid ${c.line}`,
              mb: 2.5,
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>
              {picked?.name ?? '…'}
            </Typography>
          </Box>
        ) : (
          <React.Fragment>
            {all.length > 6 && (
              <Box sx={{ mb: 1, '& > *': { maxWidth: 'none' } }}>
                <SearchBox
                  value={search}
                  onChange={setSearch}
                  placeholder='Search services'
                />
              </Box>
            )}
            <Box
              role='radiogroup'
              aria-label='Service'
              sx={{
                display: 'grid',
                gap: 0.75,
                maxHeight: 260,
                overflowY: 'auto',
                mb: 2.5,
              }}
            >
              {!q.data && (
                <Typography sx={{ color: c.muted, fontSize: 14 }}>
                  Loading…
                </Typography>
              )}
              {q.data && shown.length === 0 && (
                <Typography sx={{ color: c.muted, fontSize: 14 }}>
                  No services match.
                </Typography>
              )}
              {shown.map((s) => {
                const sel = s.id === serviceID
                const first = firstNotified(s)
                return (
                  <Box
                    key={s.id}
                    role='radio'
                    aria-checked={sel}
                    tabIndex={0}
                    onClick={() => setServiceID(s.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        setServiceID(s.id)
                      }
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      px: 1.5,
                      py: 1,
                      borderRadius: '12px',
                      cursor: 'pointer',
                      border: `1.5px solid ${sel ? c.ink : c.line}`,
                      bgcolor: sel ? c.soft : 'transparent',
                      '&:hover': { bgcolor: c.hover },
                    }}
                  >
                    <Box
                      aria-hidden
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        flexShrink: 0,
                        display: 'grid',
                        placeItems: 'center',
                        border: `1.5px solid ${sel ? c.ink : c.muted}`,
                        bgcolor: sel ? c.ink : 'transparent',
                        color: c.surface,
                        '& svg': { fontSize: 12 },
                      }}
                    >
                      {sel && <Check />}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: 14 }} noWrap>
                        {s.name}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: c.muted }} noWrap>
                        {first.length
                          ? `Notifies ${first.join(', ')} first`
                          : `Via ${s.escalationPolicy?.name ?? 'no escalation policy'}`}
                      </Typography>
                    </Box>
                    <SeverityChip name={s.name} />
                  </Box>
                )
              })}
            </Box>
          </React.Fragment>
        )}

        <TextField
          fullWidth
          label='What is wrong?'
          placeholder='e.g. Payments API returns 500 for card checkouts'
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          inputProps={{ maxLength: 255 }}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          multiline
          minRows={3}
          label='Details (optional)'
          placeholder='Links, what you already tried, who is affected…'
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          helperText='Shown on the alert page. Markdown works.'
        />

        {m.error && (
          <Alert severity='error' sx={{ mt: 2 }}>
            {m.error.message.replace('[GraphQL] ', '')}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Typography
          sx={{ fontSize: 13, color: c.muted, mr: 'auto', minWidth: 0 }}
          noWrap
        >
          {picked
            ? who.length
              ? `Notifies ${who.join(', ')} first`
              : `Goes to ${picked.name}`
            : 'Pick a service'}
        </Typography>
        <Button onClick={props.onClose}>Cancel</Button>
        <Button
          variant='contained'
          disabled={!valid || m.fetching}
          onClick={submit}
        >
          Raise alert
        </Button>
      </DialogActions>
    </Dialog>
  )
}
